import './App.css';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { APP_ROUTES } from "./config/appRoutes";
import ProtectedRoute from './config/ProtectedRoute';
import Restricted from './config/Restricted';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { autoLoginThunk } from './features/auth/authThunk';
import { getToken, clearToken } from './features/auth/authHelpers';
import { CircularProgress, Box } from '@mui/material';

// general Thunks
import { departmentThunk, branchDepartmentThunk, branchThunk } from './components/general/generalThunk';
import { getOrganizationsThunk } from "./features/contact/contactThunk";
import { getTagsThunk } from "./features/contact/contactThunk.js";

// Core pages
import Auth from './features/auth/components/auth';
import NavBar from './components/navBar/navBar';
import PageNotFound from './components/PageNotFound/PageNotFound';

// Features
import DashBoard from './components/dashBoard/dashBoard';

// On Boarding (Facebook / WhatsApp Embedded Signup)
import OnBoardingHome from './features/onBoarding/components/onBoardingHome';

// Employes
import EmployeeList from './features/employees/components/employeeList';
import EmployeeCreation from './features/employees/components/employeeCreation';

// QrCode
import QRCodeCreation from './features/qrCode/components/qrCodeCreation';
import QRCodeList from './features/qrCode/components/qrCodeList';

// Templates
import TemplateSteper from './features/templates/components/templateSteper.jsx';
import TemplatesList from './features/templates/components/templatesList';
import TemplateCreationFlow from './features/templates/components/templateCreationFlow.jsx';

// Campaigns
import CampaignList from './features/campaign/components/campaignList';
import CampaignSteper from './features/campaign/components/camPaignSteper.jsx';
import ViewCampaign from './features/campaign/components/viewCampaignDetails.jsx';

// Messages
import WhatsAppLayout from './features/messages/components/whatsAppLayout';

// ChatBot
import ChatBotCreation from './features/chatBot/components/chatBotCreation';
import ChatBotList from './features/chatBot/components/chatBotList';
import BotFlow from './features/chatBot/components/botflow/Index.js'

// File Upload
import FilesCreation from './features/files/components/files/filesCreation';
import FilesList from './features/files/components/files/filesList';
import CategoryCreation from './features/files/components/category/categoryCreation';
import CategoryList from './features/files/components/category/categoryList';

// Contacts
import ContactList from './features/contact/components/contactList';
import ContactCreation from './features/contact/components/contactCreation';
import FormList from './features/form/formList.jsx';
import FormCreation from './features/form/formCreation.jsx';

// Branches
import OrganizationList from './features/contactOrganization/compnents/organizationList.jsx';
import OrganizationCreation from './features/contactOrganization/compnents/organizationCreation.jsx';

// Branchs
import BranchList from './features/branchDepartment/components/branch/branchList';
import BranchCreation from './features/branchDepartment/components/branch/branchCreation';
import DepartmentCreation from './features/branchDepartment/components/department/departmentCreation';
import DepartmentList from './features/branchDepartment/components/department/departmentList';
import BranchDepartmentCreation from './features/branchDepartment/components/branchDepartment/branchDepartmentCreation';
import BranchDepartmentList from './features/branchDepartment/components/branchDepartment/branchDepartmentList';

// Profile Route
import UserProfile from "./components/userProfile/userProfile";

// LEAD MANAGEMENT
import MyTask from './features/leadMangement/components/myTask/myTask';
import MyTeamTask from './features/leadMangement/components/myTeamTask/myTeamTask';
import LeadManagement from './features/leadMangement/components/leadManagement/leadMangement';
import UserManagement from './features/leadMangement/components/userManagement/userMangement';
import TagsList from './features/leadMangement/components/tag/tagsList';
import TagCreation from './features/leadMangement/components/tag/tagCreation';

//Crm Quotation
import CrmQuotationList from './features/crm-quotations/crm-Quotation/crmQuotationList.jsx';
import CrmQuotationCretion from './features/crm-quotations/crm-Quotation/crmQuotationCreation.jsx';
import CrmViewQuotation from './features/crm-quotations/crm-Quotation/crmViewQuotations.jsx';

// Appointment Slot Bookings
import AppointmentList from './features/appointmentSlotBooking/components/appointmentHistory/appointmentList.jsx';
import AppointmentCreation from './features/appointmentSlotBooking/components/appointmentHistory/appointmentCreation.jsx';
import PatientHistory from './features/appointmentSlotBooking/components/appointmentHistory/patientHistory.jsx';

import AssignSlotsList from './features/appointmentSlotBooking/components/assignSlots/assignSlotList.jsx';
import AssignSlotCreation from './features/appointmentSlotBooking/components/assignSlots/assignSlotCreation.jsx';

import SlotTimingList from './features/appointmentSlotBooking/components/slotTimings/slotTimingList.jsx';
import SlotTimingCreation from './features/appointmentSlotBooking/components/slotTimings/slotTimingCreation.jsx';

function App() {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const verifyTokenAndFetchData = async () => {
      try {
        const token = getToken();
        if (token) {
          await dispatch(autoLoginThunk());

          // Run independent data-fetching thunks in parallel. Use allSettled
          // so a failure in one won't prevent the app from rendering.
          try {
            const initialFetches = [
              dispatch(departmentThunk()),
              dispatch(branchDepartmentThunk()),
              dispatch(branchThunk()),
              dispatch(getOrganizationsThunk()),
              dispatch(getTagsThunk()),
            ];

            const results = await Promise.allSettled(initialFetches);
            const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value && r.value.error));
            if (failed.length) {
              console.warn('Some initial data failed to load', failed);
            }
          } catch (err) {
            console.error('Error during initial data fetch', err);
          }
        }
      } catch (error) {
        console.error("Error in authentication or fetching data", error);
        clearToken();
      } finally {
        setLoading(false);
      }
    };

    verifyTokenAndFetchData();
  }, [dispatch]);

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path={APP_ROUTES.ROOT} element={<Auth />} />
          <Route path={APP_ROUTES.LOGIN} element={<Auth />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<NavBar />}>
              <Route path={APP_ROUTES.DASHBOARD} element={<DashBoard />} />
              <Route path={APP_ROUTES.ONBOARDING} element={<OnBoardingHome />} />
              <Route path={APP_ROUTES.PROFILE} element={<UserProfile />} />
              <Route path={APP_ROUTES.EMPLOYEE.LIST} element={<EmployeeList />} />
              <Route path={APP_ROUTES.EMPLOYEE.CREATION} element={<EmployeeCreation />} />

              <Route element={<Restricted moduleKey={'qrGeneration'} />}>
                <Route path={APP_ROUTES.QRCODE.CREATION} element={<QRCodeCreation />} />
                <Route path={APP_ROUTES.QRCODE.LIST} element={<QRCodeList />} />
              </Route>

              <Route element={<Restricted moduleKey={'templates'} />}>
                <Route path={APP_ROUTES.TEMPLATE.LIST} element={<TemplatesList />} />
                <Route path={APP_ROUTES.TEMPLATE.CREATION} element={<TemplateSteper />} />
                <Route path={APP_ROUTES.TEMPLATE.TEMPLATE_FLOW} element={<TemplateCreationFlow />} />
              </Route>

              <Route element={<Restricted moduleKey={'outboundCampaign'} />}>
                <Route path={APP_ROUTES.CAMPAIGN.CREATION} element={<CampaignSteper />} />
                <Route path={APP_ROUTES.CAMPAIGN.LIST} element={<CampaignList />} />
                <Route path={APP_ROUTES.CAMPAIGN.VIEW_CAMPAIGN} element={<ViewCampaign />} />
              </Route>

              <Route element={<Restricted moduleKey={'messages'} />}>
                <Route path={APP_ROUTES.MESSAGES} element={<WhatsAppLayout />} />
              </Route>

              <Route element={<Restricted moduleKey={'chatbot'} />}>
                <Route path={APP_ROUTES.CHATBOT.LIST} element={<ChatBotList />} />
                <Route path={APP_ROUTES.CHATBOT.CHATBOT_FLOW} element={<BotFlow />} />
                <Route path={APP_ROUTES.CHATBOT.CREATION} element={<ChatBotCreation />} />
              </Route>


              <Route element={<Restricted moduleKey={'leadGeneration'} />}>
                <Route path={APP_ROUTES.LEADMANAGEMENT.MY_TASK} element={<MyTask />} />
                <Route path={APP_ROUTES.LEADMANAGEMENT.MY_TEAM_TASK} element={<MyTeamTask />} />
                <Route path={APP_ROUTES.LEADMANAGEMENT.LEADMANAGEMENT} element={<LeadManagement />} />
                <Route path={APP_ROUTES.LEADMANAGEMENT.USER_LEADMANAGEMENT} element={<UserManagement />} />
              
              </Route>

              <Route element={<Restricted moduleKey={'filecategory'} />}>
                <Route path={APP_ROUTES.FILES.LIST} element={<FilesList />} />
                <Route path={APP_ROUTES.FILES.CREATION} element={<FilesCreation />} />
              </Route>

              <Route element={<Restricted moduleKey={'filecategory'} />}>
                <Route path={APP_ROUTES.CATEGORY.LIST} element={<CategoryList />} />
                <Route path={APP_ROUTES.CATEGORY.CREATION} element={<CategoryCreation />} />
              </Route>

              <Route element={<Restricted moduleKey={'contacts'} />}>
                <Route path={APP_ROUTES.CONTACT.LIST} element={<ContactList />} />
                <Route path={APP_ROUTES.CONTACT.CREATION} element={<ContactCreation />} />
                  <Route path={APP_ROUTES.TAGS.LIST} element={<TagsList />} />
                <Route path={APP_ROUTES.TAGS.CREATION} element={<TagCreation />} />
              </Route>


              <Route element={<Restricted moduleKey={'department'} />}>
                <Route path={APP_ROUTES.ORGANIZATION.LIST} element={<OrganizationList />} />
                <Route path={APP_ROUTES.ORGANIZATION.CREATION} element={<OrganizationCreation />} />
                <Route path={APP_ROUTES.FORM.LIST} element={<FormList />} />
                <Route path={APP_ROUTES.FORM.CREATION} element={<FormCreation />} />
                <Route path={APP_ROUTES.BRANCH.CREATION} element={<BranchCreation />} />
                <Route path={APP_ROUTES.BRANCH.LIST} element={<BranchList />} />
                <Route path={APP_ROUTES.DEPARTMENT.CREATION} element={<DepartmentCreation />} />
                <Route path={APP_ROUTES.DEPARTMENT.LIST} element={<DepartmentList />} />
                <Route path={APP_ROUTES.BRANCH_DEPARTMENT.CREATION} element={<BranchDepartmentCreation />} />
                <Route path={APP_ROUTES.BRANCH_DEPARTMENT.LIST} element={<BranchDepartmentList />} />
              </Route>


              <Route element={<Restricted moduleKey={'quotationCreation'} />}>
                <Route path={APP_ROUTES.QUOTATION.LIST} element={<CrmQuotationList />} />
                <Route path={APP_ROUTES.QUOTATION.CREATION} element={<CrmQuotationCretion />} />
                <Route path={APP_ROUTES.QUOTATION.QUOTATION_LIST} element={<CrmViewQuotation />} />
              </Route>


              <Route element={<Restricted moduleKey={'slot_booking'} />}>
                <Route path={APP_ROUTES.APPOINTMENTS.LIST} element={<AppointmentList />} />
                <Route path={APP_ROUTES.APPOINTMENTS.CREATION} element={<AppointmentCreation />} />
                <Route path={APP_ROUTES.APPOINTMENTS.PATIENT_HISTORY} element={<PatientHistory />} />


                <Route path={APP_ROUTES.ASSIGN_SLOTS.LIST} element={<AssignSlotsList />} />
                <Route path={APP_ROUTES.ASSIGN_SLOTS.CREATION} element={<AssignSlotCreation />} />

                <Route path={APP_ROUTES.SLOTS_TIMINGS.LIST} element={<SlotTimingList />} />
                <Route path={APP_ROUTES.SLOTS_TIMINGS.CREATION} element={<SlotTimingCreation />} />
              </Route>

            </Route>
          </Route>

          {/* 404 fallback */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
